import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card, Badge, SectionTitle } from "@/components/ui-hud";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plane, Play, Square, Clock, MapPin, Gauge, Navigation2 } from "lucide-react";
import BackToCockpit from "@/components/BackToCockpit";

/**
 * PJP Live Logbook — phone-driven GPS tracker.
 * State machine (server-authoritative):
 *   idle    → (>100 mph sustained ≥ 3s) → in_air:  block_out = takeoff − 6 min
 *   in_air  → (< 25 mph)                → landed:  block_in  = landing + 6 min
 */
export default function Logbook() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [active, setActive] = useState(null);            // active session
  const [speedMph, setSpeedMph] = useState(0);
  const [alt, setAlt] = useState(0);
  const [primary, setPrimary] = useState((user?.aircraft_types || [])[0] || "");
  const [buf, setBuf] = useState([]);
  const watchIdRef = useRef(null);
  const ingestTimerRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/logbook/mine");
      setSessions(r.data.sessions || []);
      setTotalHours(r.data.total_hours || 0);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { if (user) load(); }, [user]);

  // Ingest buffered GPS points every 5s
  useEffect(() => {
    if (!active) return;
    ingestTimerRef.current = setInterval(async () => {
      if (buf.length === 0) return;
      const pts = [...buf];
      setBuf([]);
      try {
        const r = await api.post("/logbook/session/ingest", { session_id: active.id, points: pts });
        setActive(r.data);
      } catch (e) { /* ignore transient */ }
    }, 5000);
    return () => clearInterval(ingestTimerRef.current);
  }, [active, buf]);

  const start = async () => {
    if (!("geolocation" in navigator)) { toast.error("GPS not available on this device"); return; }
    try {
      const r = await api.post("/logbook/session/start", { primary_aircraft_type: primary, source: "pjp_live" });
      setActive(r.data);
      const wid = navigator.geolocation.watchPosition(
        (pos) => {
          const s = pos.coords.speed || 0; // m/s
          const mph = s * 2.23694;
          setSpeedMph(mph);
          setAlt(pos.coords.altitude || 0);
          setBuf((b) => [...b, {
            lat: pos.coords.latitude, lon: pos.coords.longitude,
            alt_ft: (pos.coords.altitude || 0) * 3.28084,
            speed_mph: mph, heading_deg: pos.coords.heading || null,
            ts: new Date().toISOString(),
          }]);
        },
        (err) => toast.error("GPS: " + err.message),
        { enableHighAccuracy: true, maximumAge: 1000, timeout: 15000 },
      );
      watchIdRef.current = wid;
      toast.success("Logbook tracking started");
    } catch (e) { toast.error(e?.response?.data?.detail || "Start failed"); }
  };

  const stop = async () => {
    if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
    watchIdRef.current = null;
    // Final flush
    if (buf.length > 0 && active) {
      try { await api.post("/logbook/session/ingest", { session_id: active.id, points: buf }); } catch {}
    }
    setActive(null);
    setSpeedMph(0);
    setBuf([]);
    toast.success("Session saved");
    load();
  };

  const stateChip = (s) => {
    if (s === "in_air")  return <Badge tone="avail" pulse>In Air</Badge>;
    if (s === "landed")  return <Badge tone="neutral">Landed</Badge>;
    return <Badge tone="neutral">Idle</Badge>;
  };

  if (!user) return <div className="max-w-4xl mx-auto px-6 py-16">Auth required</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <BackToCockpit />
      <SectionTitle overline="PJP Live Logbook" title="Your flight time — captured while you fly." subtitle="No paperwork. GPS + our state machine records block out, takeoff, landing, and block in automatically." />

      {/* Live panel */}
      <Card testId="logbook-live" className="hud-corners relative mb-6">
        <span className="hud-c-bl" /><span className="hud-c-br" />
        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <div className="eyebrow-muted">Current Session</div>
              {active ? stateChip(active.state) : <Badge tone="neutral">Idle</Badge>}
              {primary && <span className="chip chip-neutral">Primary: {primary}</span>}
            </div>
            <div className="grid grid-cols-3 gap-6 mt-2">
              <div>
                <div className="text-[11px] text-white/45 uppercase tracking-widest mono mb-1"><Gauge className="h-3 w-3 inline mr-1" /> GS</div>
                <div className="font-display text-4xl text-white">{speedMph.toFixed(0)}<span className="text-white/50 text-base ml-1">mph</span></div>
              </div>
              <div>
                <div className="text-[11px] text-white/45 uppercase tracking-widest mono mb-1"><Navigation2 className="h-3 w-3 inline mr-1" /> ALT</div>
                <div className="font-display text-4xl text-white">{(alt * 3.28084).toFixed(0)}<span className="text-white/50 text-base ml-1">ft</span></div>
              </div>
              <div>
                <div className="text-[11px] text-white/45 uppercase tracking-widest mono mb-1"><Clock className="h-3 w-3 inline mr-1" /> ELAP</div>
                <div className="font-display text-4xl text-white">
                  {active?.takeoff_ts ? Math.max(0, Math.floor((Date.now() - new Date(active.takeoff_ts).getTime()) / 60000)) : 0}
                  <span className="text-white/50 text-base ml-1">m</span>
                </div>
              </div>
            </div>
            {active && (
              <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px] mono">
                <TSChip label="BLOCK OUT" v={active.block_out_ts} />
                <TSChip label="TAKEOFF"   v={active.takeoff_ts} />
                <TSChip label="LANDING"   v={active.landing_ts} />
                <TSChip label="BLOCK IN"  v={active.block_in_ts} />
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 min-w-[160px]">
            {!active && (
              <select data-testid="logbook-aircraft" className="input !py-2 text-sm" value={primary} onChange={(e)=>setPrimary(e.target.value)}>
                <option value="">Select aircraft…</option>
                {(user.aircraft_types || []).slice().sort((a, b) => a.localeCompare(b)).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
            {active ? (
              <button data-testid="btn-stop-logbook" onClick={stop} className="btn btn-outline-danger">
                <Square className="h-4 w-4" /> End Session
              </button>
            ) : (
              <button data-testid="btn-start-logbook" onClick={start} className="btn btn-primary">
                <Play className="h-4 w-4" /> Start Tracking
              </button>
            )}
          </div>
        </div>
      </Card>

      {/* Totals */}
      <div className="grid md:grid-cols-3 gap-5 mb-8">
        <Card><div className="eyebrow-muted mb-2">Total Hours (PJP-tracked)</div><div className="font-display text-4xl text-sky">{totalHours}</div></Card>
        <Card><div className="eyebrow-muted mb-2">Sessions</div><div className="font-display text-4xl text-sky">{sessions.length}</div></Card>
        <Card><div className="eyebrow-muted mb-2">Primary Aircraft</div><div className="font-display text-2xl text-white">{primary || "—"}</div></Card>
      </div>

      {/* Session history */}
      <div className="eyebrow mb-4">Session History</div>
      <div className="space-y-3">
        {sessions.length === 0 && <div className="text-white/50 text-sm">No sessions yet.</div>}
        {sessions.map((s) => (
          <Card key={s.id} className="!p-5">
            <div className="flex items-center gap-6 flex-wrap">
              <div className="min-w-[120px]">
                <div className="eyebrow-muted mb-1">Started</div>
                <div className="mono text-sm">{new Date(s.started_at).toLocaleString()}</div>
              </div>
              <div>{stateChip(s.state)}</div>
              {s.primary_aircraft_type && <span className="chip">{s.primary_aircraft_type}</span>}
              <div className="ml-auto flex items-center gap-6">
                <div><div className="eyebrow-muted mb-1">Block Out</div><div className="mono text-xs">{fmt(s.block_out_ts)}</div></div>
                <div><div className="eyebrow-muted mb-1">Block In</div><div className="mono text-xs">{fmt(s.block_in_ts)}</div></div>
                <div><div className="eyebrow-muted mb-1">Duration</div><div className="mono text-sm text-sky">{s.duration_min ? (s.duration_min/60).toFixed(1)+"h" : "—"}</div></div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function TSChip({ label, v }) {
  return (
    <div className="border border-white/[0.08] rounded-lg px-3 py-2 bg-white/[0.02]">
      <div className="text-[9px] text-white/45 mb-0.5 tracking-widest">{label}</div>
      <div className="text-white/90 text-[11px]">{fmt(v)}</div>
    </div>
  );
}
function fmt(v) { if (!v) return "—"; try { return new Date(v).toLocaleTimeString(undefined, {hour:"2-digit", minute:"2-digit", second:"2-digit"}); } catch { return "—"; } }
